import { SearchOutlined, WarningOutlined } from "@ant-design/icons";
import type { TreeProps } from "antd";
import { Alert, Input, Spin, Tree, Typography } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useMemo, useState } from "react";

import type { EntityTreeNode, TransferableEntity } from "../../types/ConfigurationTransfer";

const { Text } = Typography;

interface TransferableEntitiesTreeProps {
    /**
     * List of transferable entities from the API
     */
    entities: TransferableEntity[];

    /**
     * Currently checked tree node keys
     */
    checkedKeys: React.Key[];

    /**
     * Callback when tree selection changes
     */
    onCheck: TreeProps["onCheck"];

    /**
     * Whether the tree is disabled (e.g., when "Export All" is checked)
     */
    disabled?: boolean;

    /**
     * Loading state
     */
    loading?: boolean;
}

/**
 * Renders the title for a tree node with dependency and children indicators
 */
const renderNodeTitle = (entity: TransferableEntity, isDependency: boolean): React.ReactNode => {
    const depCount = entity.dependencies.length;
    const childCount = entity.children.length;

    return (
        <span style={{ userSelect: "none" }}>
            <Text strong={!isDependency} type={isDependency ? "secondary" : undefined}>
                {entity.displayName}
                {isDependency && <Text type="secondary"> (dependency)</Text>}
            </Text>
            {depCount > 0 && !isDependency && (
                <Text style={{ marginLeft: 8 }} type="warning">
                    <WarningOutlined /> {depCount}{" "}
                    {depCount === 1
                        ? i18n.t("configurationTransfer.tree.dependencyIndicator", { count: depCount })
                        : i18n.t("configurationTransfer.tree.dependenciesIndicator", { count: depCount })}
                </Text>
            )}
            {childCount > 0 && !isDependency && (
                <Text style={{ marginLeft: 8, color: "#1890ff" }}>
                    ℹ️ {childCount}{" "}
                    {childCount === 1
                        ? i18n.t("configurationTransfer.tree.childIndicator", { count: childCount })
                        : i18n.t("configurationTransfer.tree.childrenIndicator", { count: childCount })}
                </Text>
            )}
        </span>
    );
};

/**
 * Transforms flat entity list into hierarchical tree structure
 */
const transformToTreeData = (entities: TransferableEntity[]): EntityTreeNode[] => {
    const entityMap = new Map<string, TransferableEntity>();
    entities.forEach((entity) => entityMap.set(entity.tableName, entity));

    const treeData: EntityTreeNode[] = [];
    const processedAsParent = new Set<string>();

    // Build top-level nodes (entities that aren't dependencies of others shown)
    entities.forEach((entity) => {
        if (!entity.allowed) {
            return; // Skip disallowed entities
        }

        const children: EntityTreeNode[] = [];

        // Add dependencies as children (showing what's required)
        entity.dependencies.forEach((depName) => {
            const depEntity = entityMap.get(depName);
            if (depEntity) {
                children.push({
                    key: `${entity.tableName}-dep-${depName}`,
                    title: renderNodeTitle(depEntity, true),
                    disabled: !depEntity.allowed,
                    isDependency: true,
                    entity: depEntity,
                });
            }
        });

        const node: EntityTreeNode = {
            key: entity.tableName,
            title: renderNodeTitle(entity, false),
            disabled: !entity.allowed,
            children: children.length > 0 ? children : undefined,
            entity,
        };

        treeData.push(node);
        processedAsParent.add(entity.tableName);
    });

    return treeData;
};

/**
 * Filters tree nodes based on search value
 */
const filterTreeNodes = (nodes: EntityTreeNode[], searchValue: string): EntityTreeNode[] => {
    if (!searchValue) {
        return nodes;
    }

    const lowerSearch = searchValue.toLowerCase();

    const filterNode = (node: EntityTreeNode): EntityTreeNode | null => {
        const entity = node.entity;
        const titleMatch = entity?.displayName.toLowerCase().includes(lowerSearch) || entity?.tableName.toLowerCase().includes(lowerSearch);

        const filteredChildren = node.children?.map(filterNode).filter((n): n is EntityTreeNode => n !== null) || [];

        if (titleMatch || filteredChildren.length > 0) {
            return {
                ...node,
                children: filteredChildren.length > 0 ? filteredChildren : undefined,
            };
        }

        return null;
    };

    return nodes.map(filterNode).filter((n): n is EntityTreeNode => n !== null);
};

/**
 * Gets all keys from tree nodes recursively
 */
const getAllKeys = (nodes: EntityTreeNode[]): React.Key[] => {
    const keys: React.Key[] = [];
    const traverse = (node: EntityTreeNode) => {
        // Only include non-dependency nodes as selectable keys
        if (!node.isDependency) {
            keys.push(node.key);
        }
        node.children?.forEach(traverse);
    };
    nodes.forEach(traverse);
    return keys;
};

/**
 * TransferableEntitiesTree Component
 *
 * Displays transferable entities in a hierarchical tree structure with:
 * - Checkable nodes for selection
 * - Visual indicators for dependencies and children
 * - Search/filter functionality
 * - Disabled state support
 */
export const TransferableEntitiesTree: FC<TransferableEntitiesTreeProps> = ({
    entities,
    checkedKeys,
    onCheck,
    disabled = false,
    loading = false,
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [autoExpandParent, setAutoExpandParent] = useState(true);

    // Transform entities to tree structure
    const treeData = useMemo(() => transformToTreeData(entities), [entities]);

    // Filter tree based on search
    const filteredTreeData = useMemo(() => filterTreeNodes(treeData, searchValue), [treeData, searchValue]);

    // Handle search input
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearchValue(value);

        if (value) {
            // Auto-expand all nodes when searching
            const allKeys = getAllKeys(treeData);
            setExpandedKeys(allKeys);
            setAutoExpandParent(true);
        } else {
            setAutoExpandParent(false);
        }
    };

    const handleExpand = (keys: React.Key[]) => {
        setExpandedKeys(keys);
        setAutoExpandParent(false);
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Spin tip={i18n.t("common.loading")} />
            </div>
        );
    }

    if (entities.length === 0) {
        return <Alert message={i18n.t("configurationTransfer.tree.noEntities")} type="info" showIcon />;
    }

    return (
        <div>
            {/* Search Input */}
            <Input
                placeholder={i18n.t("configurationTransfer.modal.searchPlaceholder")}
                prefix={<SearchOutlined />}
                onChange={handleSearch}
                disabled={disabled}
                style={{ marginBottom: 16 }}
                allowClear
            />

            {/* Dependency Info Alert */}
            <Alert
                message={i18n.t("configurationTransfer.tree.dependencyInfo.title")}
                description={i18n.t("configurationTransfer.tree.dependencyInfo.description")}
                type="info"
                showIcon
                closable
                style={{ marginBottom: 16 }}
            />

            {/* Tree Component */}
            <Tree
                checkable
                checkStrictly={false} // Enable cascading selection
                disabled={disabled}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                onExpand={handleExpand}
                checkedKeys={checkedKeys}
                onCheck={onCheck}
                treeData={filteredTreeData}
                height={400}
                style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    padding: 8,
                    backgroundColor: disabled ? "#f5f5f5" : "#ffffff",
                }}
            />

            {/* Selection Count */}
            {!disabled && (
                <Alert
                    message={i18n.t("configurationTransfer.modal.selectedCount", { count: checkedKeys.length })}
                    type={checkedKeys.length > 0 ? "success" : "warning"}
                    showIcon
                    style={{ marginTop: 16 }}
                />
            )}
        </div>
    );
};
